import * as React from 'react';
import isEmpty from 'lodash/isEmpty';

import Spinner from 'components/spinner/Spinner';
import AddZalandoBrandFormFields from './AddZalandoBrandFormFields';
import AddZalandoBrandFormNotification from './AddZalandoBrandFormNotification';

import useIsLoading from 'hooks/use-is-loading';

import { logError } from 'utils/log';
import { applyTranslations } from 'utils/language';
import { API, post } from 'utils/api';
import getInstagramName from '../../utils/get-instagram-name';
import isCommaSeparatedList from '../../utils/is-comma-separated-list';

import './add-zalando-brand-form.scss';

import type { Notification } from '../../types/Notification.type';

type State = {
  text: string,
  notification: Notification
};

applyTranslations(require('./add-zalando-brand-form.i18n.json'));

const sendBrands = (brands: Array<string>): Promise<void> => post(API.END_POINTS.MISC.addBrands(), { data: brands }, { suppressGlobalNotification: true });

const getOnSubmit = (
  state: State,
  setLoading: (a: boolean) => Promise<void>,
  setState: (a: State) => void
): () => Promise<void> => async () => {
  if (state.text.length === 0) {
    return;
  }

  setLoading(true);

  try {
    const brands = state.text
      .split(',')
      .map(val => getInstagramName(val.trim()));
    const response = await sendBrands(brands);
    const notification: Notification = !isEmpty(response)
      ? { type: 'success', shown: false }
      : { type: 'error', source: 'server' };

    setState({ notification, text: '' });
  } catch (error) {
    logError(error);
    setState({
      text: state.text,
      notification: { type: 'error', source: 'server' }
    });
  } finally {
    setLoading(false);
  }
};

const getOnChange = (setState: (a: State) => void): (a: string) => void => text => {
  const notification: Notification = isCommaSeparatedList(text)
    ? { type: 'none' }
    : { type: 'error', source: 'user-input' };

  setState({ text, notification });
};

const AddZalandoBrandForm = () => {
  const [isLoading, setIsLoading] = useIsLoading();
  const [state, setState] = React.useState<State>({
    text: '',
    notification: { type: 'none' }
  });
  const onChange = getOnChange(setState);
  const onSubmit = getOnSubmit(state, setIsLoading, setState);

  return (
    <div className="add-zalando-brand-form">
      {isLoading && <Spinner />}
      <AddZalandoBrandFormFields
        disabled={Number(state.text) === 0 || state.notification.type === 'error'}
        text={state.text}
        onChange={onChange}
        onSubmit={onSubmit}
      />
      <AddZalandoBrandFormNotification
        notification={state.notification}
        onHide={() => {
          if (state.notification.type !== 'success') {
            return;
          }

          setState({ ...state, notification: { ...state.notification, shown: true } });
        }}
      />
    </div>
  );
};

export default AddZalandoBrandForm;
